import { defineStore } from 'pinia';
import { ref } from 'vue';
import { FileService } from '@/services/file.services';
import type {
  IFileImage,
  IFileResponse,
  IFileUpdateBody,
} from '@/types/file.types';
import { form_CommerceBodyUpdate } from '@/refs/commerce.refs';

/**
 * Store pour gérer les produits et leurs actions associées
 */
export const useFileStore = defineStore('file', () => {
  /**
   * État réactif contenant la liste des produits récupérés depuis l’API.
   *
   * @type {Ref<IFileImage | null>}
   */
  const datagetFile = ref<any>(null);

  /**
   * Téléverse (upload) une image liée à un produit ou fichier depuis le store.
   *
   * Cette fonction :
   * - Appelle le service `FileService.uploadImage` pour envoyer le fichier au serveur
   * - Transmet les données nécessaires pour l’upload (fichier, désignation, restaurantId, etc.)
   * - Retourne la réponse du serveur après traitement
   * - En cas d’erreur, propage l’exception pour être gérée au niveau supérieur
   *
   * @function uploadImage
   * @param {IFileImage} data - Données du fichier à téléverser, incluant :
   *  - file : le fichier image à envoyer
   *  - designation : nom ou description de l’image
   *  - restaurantId : identifiant du restaurant lié à l’image
   *
   * @returns {Promise<any>} Une promesse contenant la réponse de l’API ou une erreur en cas d’échec.
   */
  const uploadImage = async (data: IFileImage): Promise<any> => {
    try {
      const response = await FileService.uploadImage(data);
      form_CommerceBodyUpdate.logoId = response?.data?.data?.id;
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Récupère la liste des fichiers (images, documents, etc.) depuis le service distant
   * et met à jour l’état réactif `datagetFile` avec les données obtenues.
   *
   * Cette fonction :
   * - Appelle le service `FileService.getFiles` pour récupérer la liste des fichiers
   * - Met à jour la variable réactive `datagetFile` avec les résultats
   * - Retourne les données pour un usage éventuel dans d’autres parties du store ou de l’UI
   *
   * @async
   * @function getFiles
   * @returns {Promise<any>} Une promesse contenant les données des fichiers récupérées depuis l’API.
   */
  const getFiles = async (): Promise<any> => {
    const response = await FileService.getFiles();
    datagetFile.value = response.data;
    return response.data;
  };

  /**
   * Récupère les informations d’un fichier ou produit à partir de son identifiant unique.
   * Met à jour l’état réactif `datagetFile` avec les données obtenues si nécessaire.
   *
   * Cette fonction :
   * - Appelle le service `FileService.getFileById` pour récupérer le fichier correspondant
   * - Retourne les données du fichier pour un usage dans le store ou l’UI
   * - Propage les erreurs pour être gérées au niveau supérieur
   *
   * @async
   * @function getFileById
   * @param {string} fileId - Identifiant unique du fichier à récupérer
   * @returns {Promise<IFileResponse>} Une promesse contenant les informations du fichier récupérées depuis l’API
   * ou une erreur en cas d’échec.
   */
  const getFileById = async (fileId: string): Promise<IFileResponse> => {
    try {
      const response = await FileService.getFileById(fileId);
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Met à jour les informations d’un fichier existant depuis le store.
   *
   * Cette fonction :
   * - Appelle le service `FileService.UpdateFileDetails` pour envoyer les nouvelles données au serveur
   * - Transmet l’ID du fichier et les données à mettre à jour (designation, métadonnées, etc.)
   * - Retourne la réponse du serveur après la mise à jour
   * - Propage les erreurs pour être gérées au niveau supérieur
   *
   * @async
   * @function UpdateFileDetails
   * @param {IFileUpdateBody} data - Données à appliquer lors de la mise à jour du fichier
   * @param {string} fileId - Identifiant unique du fichier à mettre à jour
   * @returns {Promise<any>} Une promesse contenant la réponse de l’API ou une erreur en cas d’échec
   */
  const UpdateFileDetails = async (
    data: IFileUpdateBody,
    fileId: string,
  ): Promise<any> => {
    try {
      const response = await FileService.UpdateFileDetails(data, fileId);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  /**
   * Supprime un fichier existant à partir de son identifiant depuis le store.
   *
   * Cette fonction :
   * - Appelle le service `FileService.DeleteFile` pour supprimer le fichier côté serveur
   * - Supprime toutes les informations associées au fichier (métadonnées, associations au restaurant, etc.)
   * - Retourne la réponse du serveur confirmant la suppression
   * - Propage les erreurs pour être gérées au niveau supérieur
   *
   * @async
   * @function deleteFile
   * @param {string} fileId - Identifiant unique du fichier à supprimer
   * @returns {Promise<any>} Une promesse contenant la réponse de l’API ou une erreur en cas d’échec
   */
  const deleteFile = async (fileId: string): Promise<any> => {
    try {
      const response = await FileService.DeleteFile(fileId);
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  return {
    datagetFile,
    uploadImage,
    getFiles,
    getFileById,
    UpdateFileDetails,
    deleteFile,
  };
});
